import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, memo } from "react";
import { Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { NotificationContext } from "../../Notifications";
import FormFieldError from "../../components/FormFieldError";
import { Table, useTable } from "../../components/Table/Table";
import FirmDefaultMtaxRates, {
  formatFirmRate,
  formatFirmBudget,
} from "../../components/FirmDefaultMtaxRates";
import {
  useAuthenticatedQuery,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import { formatCurrencyComponent } from "../../lib/numbers";

type AccountLotSelectionData = {
  accountId: number;
  accountName: string;
  isTaxable: boolean;
  lotSelectionMethod?: "HIFO" | "MTAX" | null;
  capitalGainsBudgetST?: number | null;
  capitalGainsBudgetLT?: number | null;
  stGainTaxRate?: number | null;
  ltGainTaxRate?: number | null;
  taxBudget?: number | null;
};

type AccountLotSelectionResponse = {
  firmDefaultLotSelectionMethod: "HIFO" | "MTAX";
  firmDefaultStGainTaxRate: number | null;
  firmDefaultLtGainTaxRate: number | null;
  firmDefaultTaxBudget: number | null;
  accounts: AccountLotSelectionData[];
};

type AccountFinancialData = {
  accountId: number;
  value: number;
  unrealizedGainLoss: number;
  realizedGainLoss: number;
};

const numericOrNull = (value: unknown): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const accountLotSelectionSchema = yup
  .object({
    accounts: yup
      .array()
      .of(
        yup.object().shape({
          accountId: yup.number().required(),
          isTaxable: yup.boolean().required(),
          lotSelectionMethod: yup.string().optional().nullable(),
          capitalGainsBudgetST: yup
            .number()
            .nullable()
            .when(["isTaxable", "lotSelectionMethod"], {
              is: (isTaxable: boolean, lotSelectionMethod: string) =>
                isTaxable && lotSelectionMethod === "HIFO",
              then: (schema) =>
                schema
                  .required("ST Capital Gains Budget is required for HIFO")
                  .min(0, "Must be positive"),
              otherwise: (schema) => schema.optional(),
            }),
          capitalGainsBudgetLT: yup
            .number()
            .nullable()
            .when(["isTaxable", "lotSelectionMethod"], {
              is: (isTaxable: boolean, lotSelectionMethod: string) =>
                isTaxable && lotSelectionMethod === "HIFO",
              then: (schema) =>
                schema
                  .required("LT Capital Gains Budget is required for HIFO")
                  .min(0, "Must be positive"),
              otherwise: (schema) => schema.optional(),
            }),
          stGainTaxRate: yup
            .number()
            .nullable()
            .when(["isTaxable", "lotSelectionMethod"], {
              is: (isTaxable: boolean, lotSelectionMethod: string) =>
                isTaxable && lotSelectionMethod === "MTAX",
              then: (schema) =>
                schema
                  .required("ST Tax Rate is required for MTAX")
                  .min(0, "Must be positive")
                  .max(100, "Cannot exceed 100%"),
              otherwise: (schema) => schema.optional(),
            }),
          ltGainTaxRate: yup
            .number()
            .nullable()
            .when(["isTaxable", "lotSelectionMethod"], {
              is: (isTaxable: boolean, lotSelectionMethod: string) =>
                isTaxable && lotSelectionMethod === "MTAX",
              then: (schema) =>
                schema
                  .required("LT Tax Rate is required for MTAX")
                  .min(0, "Must be positive")
                  .max(100, "Cannot exceed 100%"),
              otherwise: (schema) => schema.optional(),
            }),
          taxBudget: yup
            .number()
            .nullable()
            .when(["isTaxable", "lotSelectionMethod"], {
              is: (isTaxable: boolean, lotSelectionMethod: string) =>
                isTaxable && lotSelectionMethod === "MTAX",
              then: (schema) =>
                schema
                  .required("Tax Budget is required for MTAX")
                  .min(0, "Must be positive"),
              otherwise: (schema) => schema.optional(),
            }),
        }),
      )
      .required(),
  })
  .required();

type AccountLotSelectionFormData = yup.InferType<
  typeof accountLotSelectionSchema
>;

interface AccountLotSelectionModalProps {
  show: boolean;
  onHide: () => void;
  householdId: number;
  accounts?: Array<AccountFinancialData>;
  onSave: () => void;
}

type AccountRow = AccountLotSelectionData &
  AccountFinancialData & {
    fieldIndex: number;
  };

// Memoized cell components to prevent re-rendering and focus loss
const LotSelectionMethodCell = memo(
  ({
    account,
    register,
    handleLotSelectionMethodChange,
    firmDefaultMethod,
  }: {
    account: AccountRow;
    register: any;
    handleLotSelectionMethodChange: (index: number, method: string) => void;
    firmDefaultMethod: "HIFO" | "MTAX";
  }) => {
    const fieldIndex = account.fieldIndex;

    if (!account.isTaxable) {
      return <span className="text-muted">Non-taxable</span>;
    }

    return (
      <Form.Select
        {...register(`accounts.${fieldIndex}.lotSelectionMethod`)}
        onChange={(e) =>
          handleLotSelectionMethodChange(fieldIndex, e.target.value)
        }
        size="sm"
      >
        <option value="">Firm default ({firmDefaultMethod})</option>
        <option value="HIFO">HIFO</option>
        <option value="MTAX">MTAX</option>
      </Form.Select>
    );
  },
);

const ConfigurationFieldsCell = memo(
  ({
    account,
    register,
    errors,
    watchedMethod,
    firmDefaultMethod,
    firmDefaultStGainTaxRate,
    firmDefaultLtGainTaxRate,
    firmDefaultTaxBudget,
  }: {
    account: AccountRow;
    register: any;
    errors: any;
    watchedMethod: string;
    firmDefaultMethod: "HIFO" | "MTAX";
    firmDefaultStGainTaxRate: number | null;
    firmDefaultLtGainTaxRate: number | null;
    firmDefaultTaxBudget: number | null;
  }) => {
    const fieldIndex = account.fieldIndex;

    if (!account.isTaxable) {
      return null;
    }

    // Empty method = inherit the firm default. Show the inherited settings as
    // read-only so the advisor sees what is actually in effect, rather than
    // editable fields that would silently create an override.
    if (!watchedMethod) {
      if (firmDefaultMethod !== "MTAX") {
        return (
          <span className="text-muted small">
            Uses firm default ({firmDefaultMethod}) — no additional settings.
          </span>
        );
      }
      return (
        <FirmDefaultMtaxRates
          variant="compact"
          idPrefix={`lot-${account.accountId}`}
          stGainTaxRate={firmDefaultStGainTaxRate}
          ltGainTaxRate={firmDefaultLtGainTaxRate}
          taxBudget={firmDefaultTaxBudget}
        />
      );
    }

    if (watchedMethod === "HIFO") {
      return (
        <div className="d-flex gap-2">
          <div>
            <Form.Label className="small mb-1">ST Budget</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                {...register(`accounts.${fieldIndex}.capitalGainsBudgetST`, {
                  setValueAs: numericOrNull,
                })}
                isInvalid={
                  !!errors.accounts?.[fieldIndex]?.capitalGainsBudgetST
                }
              />
            </InputGroup>
          </div>
          <div>
            <Form.Label className="small mb-1">LT Budget</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                {...register(`accounts.${fieldIndex}.capitalGainsBudgetLT`, {
                  setValueAs: numericOrNull,
                })}
                isInvalid={
                  !!errors.accounts?.[fieldIndex]?.capitalGainsBudgetLT
                }
              />
            </InputGroup>
          </div>
        </div>
      );
    }

    if (watchedMethod === "MTAX") {
      return (
        <div className="d-flex gap-2">
          <div>
            <Form.Label className="small mb-1">ST Rate</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                step="0.01"
                {...register(`accounts.${fieldIndex}.stGainTaxRate`, {
                  setValueAs: numericOrNull,
                })}
                isInvalid={!!errors.accounts?.[fieldIndex]?.stGainTaxRate}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </div>
          <div>
            <Form.Label className="small mb-1">LT Rate</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                step="0.01"
                {...register(`accounts.${fieldIndex}.ltGainTaxRate`, {
                  setValueAs: numericOrNull,
                })}
                isInvalid={!!errors.accounts?.[fieldIndex]?.ltGainTaxRate}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </div>
          <div>
            <Form.Label className="small mb-1">Tax Budget</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                {...register(`accounts.${fieldIndex}.taxBudget`, {
                  setValueAs: numericOrNull,
                })}
                isInvalid={!!errors.accounts?.[fieldIndex]?.taxBudget}
              />
            </InputGroup>
          </div>
        </div>
      );
    }

    return null;
  },
);

const AccountLotSelectionModal = ({
  show,
  onHide,
  householdId,
  accounts: accountsWithFinancialData,
  onSave,
}: AccountLotSelectionModalProps) => {
  const notificationContext = useContext(NotificationContext);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<AccountLotSelectionFormData>({
    mode: "onBlur",
    resolver: yupResolver(accountLotSelectionSchema),
    defaultValues: {
      accounts: [],
    },
  });

  // Fetch account lot selection data
  const { data: lotSelectionData, isLoading } = useAuthenticatedQuery<
    { data: AccountLotSelectionResponse },
    AccountLotSelectionResponse,
    [string, number, string, string]
  >({
    queryKey: ["households", householdId, "accounts", "lot-selection"],
    // The key does not move; the url is under `/v1` (APP-1653).
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    queryFn: async (response) => response.data,
    queryOptions: {
      enabled: show && householdId > 0,
    },
  });

  // Merge lot selection data with financial data from props
  const accountData = useMemo(() => {
    if (!lotSelectionData?.accounts || !accountsWithFinancialData) return [];

    const financialDataMap = new Map(
      accountsWithFinancialData.map((account) => [account.accountId, account]),
    );

    return lotSelectionData.accounts.map(
      (lotData: AccountLotSelectionData) => ({
        ...lotData,
        value: financialDataMap.get(lotData.accountId)?.value ?? 0,
        unrealizedGainLoss:
          financialDataMap.get(lotData.accountId)?.unrealizedGainLoss ?? 0,
        realizedGainLoss:
          financialDataMap.get(lotData.accountId)?.realizedGainLoss ?? 0,
      }),
    );
  }, [lotSelectionData, accountsWithFinancialData]);

  // Update mutation
  const updateAccountLotSelection = useAuthenticatedMutationAsync<{
    data: AccountLotSelectionResponse;
  }>(
    `/v1/households/${householdId}/accounts/lot-selection`,
    async (data: { accounts: Array<any> }) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const saveMutation = useMutation({
    mutationFn: async (formData: AccountLotSelectionFormData) => {
      // Clean the data before submission - set irrelevant fields to null
      const cleanedData = {
        accounts: formData.accounts.map((account) => {
          const cleaned = { ...account };

          if (!account.isTaxable || !account.lotSelectionMethod) {
            // Non-taxable accounts and accounts inheriting the firm default
            // (empty method) carry no account-level lot-selection override.
            cleaned.lotSelectionMethod = undefined;
            cleaned.capitalGainsBudgetST = undefined;
            cleaned.capitalGainsBudgetLT = undefined;
            cleaned.stGainTaxRate = undefined;
            cleaned.ltGainTaxRate = undefined;
            cleaned.taxBudget = undefined;
          } else if (account.lotSelectionMethod === "HIFO") {
            // For HIFO, clear MTAX fields
            cleaned.stGainTaxRate = undefined;
            cleaned.ltGainTaxRate = undefined;
            cleaned.taxBudget = undefined;
          } else if (account.lotSelectionMethod === "MTAX") {
            // For MTAX, clear HIFO fields
            cleaned.capitalGainsBudgetST = undefined;
            cleaned.capitalGainsBudgetLT = undefined;
          }

          // Remove isTaxable from submission data as it's not needed by the API
          delete (cleaned as any).isTaxable;

          return cleaned;
        }),
      };

      await updateAccountLotSelection(cleanedData);
    },
    onSuccess: () => {
      notificationContext.pushNotification({
        id: "account-lot-selection-save",
        header: "Account Settings Updated",
        body: "Account lot selection settings have been saved successfully",
        variant: "success",
      });
      onSave();
    },
    onError: () => {
      notificationContext.pushNotification({
        id: "account-lot-selection-save",
        header: "Failed to Update Account Settings",
        body: "Failed to save account lot selection settings",
        variant: "danger",
      });
    },
  });

  const onSubmit = (data: AccountLotSelectionFormData) => {
    // Only submit accounts whose values actually changed. The PUT overwrites
    // every column for each account it receives, so sending untouched rows
    // would wipe dormant-but-live overrides (e.g. an inheriting account's own
    // HIFO capital-gains budget, which the rebalancer reads account-only).
    //
    // We compare against the originally-loaded values rather than react-hook-
    // form's dirty state: the numeric fields register a `setValueAs` transform
    // that can mark a field dirty on mount without any user edit, which would
    // make an untouched account look changed and silently clear it.
    const originalById = new Map(
      (lotSelectionData?.accounts ?? []).map((a) => [a.accountId, a]),
    );
    // The budget fields are rounded to cents when loaded into the form (see the
    // reset() below), so compare them at the same precision — otherwise an
    // untouched account whose stored value carries sub-cent precision would
    // look changed and get re-submitted, silently truncating the stored value.
    const cents = (value: unknown): number | null => {
      const n = numericOrNull(value);
      return n === null ? null : Math.round(n * 100) / 100;
    };
    const isChanged = (
      account: AccountLotSelectionFormData["accounts"][number],
    ) => {
      const original = originalById.get(account.accountId);
      if (!original) return true; // unknown account — submit to be safe
      return !(
        (account.lotSelectionMethod || "") ===
          (original.lotSelectionMethod || "") &&
        cents(account.capitalGainsBudgetST) ===
          cents(original.capitalGainsBudgetST) &&
        cents(account.capitalGainsBudgetLT) ===
          cents(original.capitalGainsBudgetLT) &&
        numericOrNull(account.stGainTaxRate) ===
          numericOrNull(original.stGainTaxRate) &&
        numericOrNull(account.ltGainTaxRate) ===
          numericOrNull(original.ltGainTaxRate) &&
        numericOrNull(account.taxBudget) === numericOrNull(original.taxBudget)
      );
    };
    // A non-taxable account is never sent: the save would null its stored
    // lot-selection settings, and those must survive a change in how the
    // account's tax type is resolved (doc-account-tax-classification §6).
    saveMutation.mutate({
      accounts: (data.accounts ?? []).filter(
        (account) => account.isTaxable && isChanged(account),
      ),
    });
  };

  const handleHide = () => {
    onHide();
  };

  // Reset form when modal opens or account data changes
  useEffect(() => {
    if (show && lotSelectionData?.accounts) {
      const formAccounts = lotSelectionData.accounts.map(
        (account: AccountLotSelectionData) => ({
          accountId: account.accountId,
          isTaxable: account.isTaxable,
          // null = inherit firm default; map to "" so the select shows the
          // "Firm default" option rather than falling back to the first option.
          lotSelectionMethod: account.lotSelectionMethod ?? "",
          capitalGainsBudgetST:
            account.capitalGainsBudgetST != null
              ? Math.round(account.capitalGainsBudgetST * 100) / 100
              : account.capitalGainsBudgetST,
          capitalGainsBudgetLT:
            account.capitalGainsBudgetLT != null
              ? Math.round(account.capitalGainsBudgetLT * 100) / 100
              : account.capitalGainsBudgetLT,
          stGainTaxRate: account.stGainTaxRate,
          ltGainTaxRate: account.ltGainTaxRate,
          taxBudget: account.taxBudget,
        }),
      );

      reset({ accounts: formAccounts });
    }
  }, [show, lotSelectionData, reset]);

  // Handle lot selection method change - clear fields when switching methods
  const handleLotSelectionMethodChange = useCallback(
    (accountIndex: number, newMethod: string) => {
      const currentAccounts = getValues("accounts");
      const updatedAccount = { ...currentAccounts[accountIndex] };

      // Clear previous method's fields
      if (!newMethod) {
        // Firm default (inherit): clear every account-level override field
        updatedAccount.capitalGainsBudgetST = undefined;
        updatedAccount.capitalGainsBudgetLT = undefined;
        updatedAccount.stGainTaxRate = undefined;
        updatedAccount.ltGainTaxRate = undefined;
        updatedAccount.taxBudget = undefined;
      } else if (newMethod === "HIFO") {
        // Clear MTAX fields
        updatedAccount.stGainTaxRate = undefined;
        updatedAccount.ltGainTaxRate = undefined;
        updatedAccount.taxBudget = undefined;
      } else if (newMethod === "MTAX") {
        // Clear HIFO fields
        updatedAccount.capitalGainsBudgetST = undefined;
        updatedAccount.capitalGainsBudgetLT = undefined;
      }

      updatedAccount.lotSelectionMethod = newMethod;

      const updatedAccounts = [...currentAccounts];
      updatedAccounts[accountIndex] = updatedAccount;
      setValue("accounts", updatedAccounts, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  // Prepare table data
  const tableData = useMemo(() => {
    if (!accountData) return [];

    return accountData.map(
      (
        account: AccountLotSelectionData & AccountFinancialData,
        index: number,
      ): AccountRow => ({
        ...account,
        fieldIndex: index,
      }),
    );
  }, [accountData]);

  const columnHelper = useMemo(() => createColumnHelper<AccountRow>(), []);

  // Watch all lot selection methods at once to avoid re-renders
  const watchedLotSelectionMethods = watch("accounts");

  // Firm defaults inherited by accounts that do not override the method.
  const firmDefaultMethod =
    lotSelectionData?.firmDefaultLotSelectionMethod ?? "HIFO";
  const firmDefaultStGainTaxRate =
    lotSelectionData?.firmDefaultStGainTaxRate ?? null;
  const firmDefaultLtGainTaxRate =
    lotSelectionData?.firmDefaultLtGainTaxRate ?? null;
  const firmDefaultTaxBudget = lotSelectionData?.firmDefaultTaxBudget ?? null;

  const columns = useMemo(
    () => [
      columnHelper.accessor("accountName", {
        header: "Account Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("value", {
        header: "Value",
        cell: (info) => formatCurrencyComponent(info.getValue()),
      }),
      columnHelper.accessor("unrealizedGainLoss", {
        header: "Unrealized Gain/Loss",
        cell: (info) => formatCurrencyComponent(info.getValue()),
      }),
      columnHelper.accessor("realizedGainLoss", {
        header: "Realized Gain/Loss",
        cell: (info) => formatCurrencyComponent(info.getValue()),
      }),
      columnHelper.accessor("lotSelectionMethod", {
        header: "Lot Selection Method",
        cell: (info) => {
          const account = info.row.original;
          return (
            <LotSelectionMethodCell
              account={account}
              register={register}
              handleLotSelectionMethodChange={handleLotSelectionMethodChange}
              firmDefaultMethod={firmDefaultMethod}
            />
          );
        },
      }),
      columnHelper.display({
        id: "fields",
        header: "Lot Selection Settings",
        cell: (info) => {
          const account = info.row.original;
          const fieldIndex = account.fieldIndex;
          const watchedMethod =
            watchedLotSelectionMethods?.[fieldIndex]?.lotSelectionMethod || "";

          return (
            <ConfigurationFieldsCell
              account={account}
              register={register}
              errors={errors}
              watchedMethod={watchedMethod}
              firmDefaultMethod={firmDefaultMethod}
              firmDefaultStGainTaxRate={firmDefaultStGainTaxRate}
              firmDefaultLtGainTaxRate={firmDefaultLtGainTaxRate}
              firmDefaultTaxBudget={firmDefaultTaxBudget}
            />
          );
        },
      }),
    ],
    [
      columnHelper,
      register,
      errors,
      handleLotSelectionMethodChange,
      watchedLotSelectionMethods,
      firmDefaultMethod,
      firmDefaultStGainTaxRate,
      firmDefaultLtGainTaxRate,
      firmDefaultTaxBudget,
    ],
  );

  const { table } = useTable({
    data: tableData,
    columns,
  });

  if (isLoading) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Lot Selection Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-5">
          <Spinner size="sm" className="me-2" />
          Loading account data...
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal show={show} onHide={handleHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Lot Selection Settings</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            {lotSelectionData && (
              <div className="mb-3 p-2 bg-body-tertiary border rounded">
                <small>
                  Firm default: {lotSelectionData.firmDefaultLotSelectionMethod}
                  {lotSelectionData.firmDefaultLotSelectionMethod ===
                    "MTAX" && (
                    <>
                      {" "}
                      &mdash; ST Rate{" "}
                      {formatFirmRate(
                        lotSelectionData.firmDefaultStGainTaxRate,
                      )}{" "}
                      | LT Rate{" "}
                      {formatFirmRate(
                        lotSelectionData.firmDefaultLtGainTaxRate,
                      )}{" "}
                      | Tax Budget{" "}
                      {formatFirmBudget(lotSelectionData.firmDefaultTaxBudget)}
                    </>
                  )}
                </small>
              </div>
            )}
            <Table table={table} disablePagination showSearchGroup={false} />
            {errors.accounts && (
              <div
                className="alert alert-danger mt-3 mb-0"
                role="alert"
                data-testid="lot-selection-errors"
              >
                <div className="fw-bold mb-1">
                  Please correct the highlighted fields before saving.
                </div>
                {!Array.isArray(errors.accounts) &&
                  (errors.accounts as { message?: string })?.message && (
                    <div>
                      {(errors.accounts as { message?: string }).message}
                    </div>
                  )}
                {Array.isArray(errors.accounts) &&
                  errors.accounts.map((accountError: any, index: number) => {
                    if (!accountError) return null;
                    const formAccount = getValues(`accounts.${index}`);
                    const accountById = accountData.find(
                      (a) => a.accountId === formAccount?.accountId,
                    );
                    const accountName =
                      accountById?.accountName ??
                      `Account ${formAccount?.accountId ?? index + 1}`;
                    return (
                      <div key={formAccount?.accountId ?? index}>
                        <div className="fw-semibold mt-2">{accountName}</div>
                        {accountError?.capitalGainsBudgetST && (
                          <FormFieldError
                            field={accountError.capitalGainsBudgetST}
                          />
                        )}
                        {accountError?.capitalGainsBudgetLT && (
                          <FormFieldError
                            field={accountError.capitalGainsBudgetLT}
                          />
                        )}
                        {accountError?.stGainTaxRate && (
                          <FormFieldError field={accountError.stGainTaxRate} />
                        )}
                        {accountError?.ltGainTaxRate && (
                          <FormFieldError field={accountError.ltGainTaxRate} />
                        )}
                        {accountError?.taxBudget && (
                          <FormFieldError field={accountError.taxBudget} />
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleHide}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AccountLotSelectionModal;
